import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Church, Search, MapPin, MapPinned, Store, Newspaper, Globe, Briefcase } from "lucide-react";
import { getLiturgicalSeason, getSeasonLogo } from "@/lib/liturgicalCalendar";

/**
 * PublicNavigation - Navigation header for non-authenticated pages
 * Used on Landing, Login, Register, and other public pages
 */
export default function PublicNavigation({ showAuthButtons = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [season] = useState(() => getLiturgicalSeason());

  const publicNavItems = [
    { path: "/sacrament-finder", label: "Sacrament Finder", icon: MapPinned },
    { path: "/marketplace", label: "Marketplace", icon: Store },
    { path: "/directory", label: "Business Directory", icon: MapPin },
    { path: "/vatican-news", label: "Vatican News", icon: Newspaper },
    { path: "/ewtn-news", label: "EWTN News", icon: Globe },
  ];

  const isActive = (path) => location.pathname === path;
  const primaryColor = season.colors?.primary || "var(--liturgical-primary)";

  return (
    <nav className="bg-white border-b border-[#E5E0D5] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={getSeasonLogo(season)} 
              alt="Parish" 
              className="w-9 h-9 rounded-lg"
            />
            <span className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26]">Parish</span>
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center gap-0.5 overflow-x-auto">
            {publicNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-2.5 py-2 rounded-md text-xs font-medium font-['Nunito'] transition-colors whitespace-nowrap ${
                  isActive(item.path)
                    ? "bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)]"
                    : "text-[#6B665E] hover:text-[#2D2A26] hover:bg-[#F9F7F2]"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Auth Buttons */}
          {showAuthButtons && (
            <div className="hidden md:flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="font-['Nunito'] text-[#6B665E] hover:text-[#2D2A26]">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button 
                  className="text-white font-['Nunito']"
                  style={{ backgroundColor: primaryColor }}
                >
                  Join Your Parish
                </Button>
              </Link>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-md text-[#6B665E] hover:bg-[#F9F7F2]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-[#E5E0D5] bg-white">
          <div className="px-4 py-3 space-y-1">
            {publicNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium font-['Nunito'] ${
                  isActive(item.path)
                    ? "bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)]"
                    : "text-[#6B665E] hover:bg-[#F9F7F2]"
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
            {showAuthButtons && (
              <div className="pt-3 border-t border-[#E5E0D5] mt-3 space-y-2">
                <Link to="/login" className="block" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full font-['Nunito']">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register" className="block" onClick={() => setMobileMenuOpen(false)}>
                  <Button 
                    className="w-full text-white font-['Nunito']"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Join Your Parish
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
